import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { adminTenantsApi, adminFoundationApi, ingestionApi } from '../services/api';
import { toast } from '../stores';
import {
  NexusCard, NexusButton, NexusInput, NexusSelect,
} from './nexus-ui';
import {
  Building2, User, Upload, CheckCircle2, ChevronRight,
  ChevronLeft, X, FileText, Loader2, XCircle, Rocket, Database,
} from 'lucide-react';

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free — 2 users, 50K tokens/mo' },
  { value: 'starter', label: 'Starter — 5 users, 500K tokens/mo' },
  { value: 'professional', label: 'Professional — 20 users, 2M tokens/mo' },
  { value: 'enterprise', label: 'Enterprise — 100 users, 10M tokens/mo' },
];

const STEPS = [
  { id: 'tenant', label: 'Organization', icon: Building2 },
  { id: 'user', label: 'Admin User', icon: User },
  { id: 'knowledge', label: 'Knowledge Base', icon: Database },
  { id: 'done', label: 'Complete', icon: Rocket },
];

const STAGE_LABELS = {
  queued: 'Queued', uploading: 'Uploading', parsing: 'Parsing',
  chunking: 'Chunking', embedding: 'Embedding', storing: 'Saving',
  completed: 'Done', error: 'Failed',
};

export default function SetupWizard({ isOpen, onClose, autoTriggered = false }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTenantId, setCreatedTenantId] = useState(null);
  const [createdTenantSlug, setCreatedTenantSlug] = useState(null);
  const [createdShareCode, setCreatedShareCode] = useState(null);
  const [createdKBId, setCreatedKBId] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const [tenantForm, setTenantForm] = useState({
    name: '', slug: '', industry: '', billing_email: '', plan_tier: 'free',
  });
  const [userForm, setUserForm] = useState({
    name: '', email: '', password: '',
  });
  const [kbForm, setKbForm] = useState({
    name: '', description: '',
  });

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  // Step 1: Create Tenant
  const handleCreateTenant = async () => {
    if (!tenantForm.name || !tenantForm.billing_email) {
      toast.error('Organization name and billing email are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await adminTenantsApi.create({
        ...tenantForm,
        slug: tenantForm.slug || autoSlug(tenantForm.name),
      });
      setCreatedTenantId(res.data.id);
      setCreatedTenantSlug(res.data.slug);
      setCreatedShareCode(res.data.share_code);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
      toast.success(`Tenant "${tenantForm.name}" created!`);
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Create Admin User
  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('All user fields are required');
      return;
    }
    if (userForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsSubmitting(true);
    try {
      await adminTenantsApi.createUser(createdTenantId, {
        ...userForm,
        role: 'admin',
      });
      toast.success(`Admin user "${userForm.name}" created!`);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 3a: Create Knowledge Base
  const handleCreateKB = async () => {
    if (!kbForm.name) {
      toast.error('Knowledge base name is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await adminFoundationApi.createKB(kbForm);
      setCreatedKBId(res.data.id);
      queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
      toast.success(`Knowledge base "${kbForm.name}" created!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create KB');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 3b: Upload Files
  const handleFilesSelected = useCallback(async (files) => {
    if (!createdKBId) {
      toast.error('Create a knowledge base first');
      return;
    }
    setIsUploading(true);
    const queue = files.map(f => ({ file: f, status: { stage: 'queued', progress: 0, detail: 'Queued' } }));
    setUploadQueue(prev => [...prev, ...queue]);

    for (let i = 0; i < files.length; i++) {
      const queueIndex = uploadQueue.length + i;
      try {
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('kb_id', createdKBId);
        formData.append('kb_type', 'foundation');
        formData.append('category', 'general');

        const res = await ingestionApi.uploadDocumentAsync(formData);
        const taskId = res.data.task_id;

        await new Promise((resolve) => {
          const interval = setInterval(async () => {
            try {
              const prog = await ingestionApi.getProgress(taskId);
              const progressData = prog.data;
              setUploadQueue(prev => prev.map((item, j) =>
                j === queueIndex ? { ...item, status: progressData } : item
              ));
              if (progressData.stage === 'completed' || progressData.stage === 'error') {
                clearInterval(interval);
                resolve();
              }
            } catch {
              clearInterval(interval);
              resolve();
            }
          }, 800);
          setTimeout(() => { clearInterval(interval); resolve(); }, 120000);
        });
      } catch (err) {
        setUploadQueue(prev => prev.map((item, j) =>
          j === queueIndex ? { ...item, status: { stage: 'error', progress: 0, detail: err.message || 'Upload failed' } } : item
        ));
      }
    }

    queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
    setIsUploading(false);
  }, [createdKBId, uploadQueue.length, queryClient]);

  const handleFileInput = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.docx,.doc,.txt';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) handleFilesSelected(files);
    };
    input.click();
  };

  const handleFinish = () => {
    onClose();
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
  };

  if (!isOpen) return null;

  const allUploadsComplete = uploadQueue.length > 0 && uploadQueue.every(q => q.status.stage === 'completed' || q.status.stage === 'error');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && !autoTriggered && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-nexus-bg-secondary border border-nexus-border-emphasized rounded-2xl"
          data-testid="setup-wizard"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-nexus-text-primary">Quick Setup Wizard</h2>
              <p className="text-sm text-nexus-text-tertiary mt-0.5">Get your first tenant up and running in minutes</p>
            </div>
            {!autoTriggered && (
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-nexus-bg-tertiary text-nexus-text-tertiary hover:text-nexus-text-primary transition-colors">
                <X size={18} />
              </button>
            )}
          </div>

          {/* Step Indicators */}
          <div className="px-6 pb-4">
            <div className="flex items-center gap-1">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === step;
                const isDone = i < step;
                return (
                  <div key={s.id} className="flex items-center flex-1">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-xs font-medium w-full
                      ${isActive ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint' : ''}
                      ${isDone ? 'text-green-400' : ''}
                      ${!isActive && !isDone ? 'text-nexus-text-tertiary' : ''}
                    `}>
                      {isDone ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                      <span className="hidden sm:inline">{s.label}</span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`w-4 h-px mx-1 flex-shrink-0 ${isDone ? 'bg-green-400' : 'bg-nexus-border-subtle'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-6 pb-6">
            {/* Step 1: Create Tenant */}
            {step === 0 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <NexusInput
                  label="Organization Name"
                  value={tenantForm.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setTenantForm(p => ({ ...p, name, slug: autoSlug(name) }));
                  }}
                  placeholder="Acme Engineering"
                  data-testid="wizard-tenant-name"
                />
                <div className="grid grid-cols-2 gap-4">
                  <NexusInput
                    label="Industry"
                    value={tenantForm.industry}
                    onChange={(e) => setTenantForm(p => ({ ...p, industry: e.target.value }))}
                    placeholder="Oil & Gas"
                  />
                  <NexusInput
                    label="Billing Email"
                    type="email"
                    value={tenantForm.billing_email}
                    onChange={(e) => setTenantForm(p => ({ ...p, billing_email: e.target.value }))}
                    placeholder="billing@acme.com"
                    data-testid="wizard-billing-email"
                  />
                </div>
                <NexusSelect
                  label="Plan Tier"
                  options={PLAN_OPTIONS}
                  value={tenantForm.plan_tier}
                  onChange={(v) => setTenantForm(p => ({ ...p, plan_tier: v }))}
                />
                <div className="flex justify-end pt-2">
                  <NexusButton onClick={handleCreateTenant} isLoading={isSubmitting} data-testid="wizard-create-tenant">
                    Create & Continue <ChevronRight size={16} />
                  </NexusButton>
                </div>
              </motion.div>
            )}

            {/* Step 2: Create Admin User */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <div className="p-3 rounded-lg bg-nexus-accent-mint/5 border border-nexus-accent-mint/20 text-sm text-nexus-text-secondary">
                  Create the first admin user for <span className="font-medium text-nexus-accent-mint">{tenantForm.name}</span>. They'll be able to log into the Portal and manage the tenant's knowledge base and settings.
                </div>
                <NexusInput
                  label="Full Name"
                  value={userForm.name}
                  onChange={(e) => setUserForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="John Doe"
                  data-testid="wizard-user-name"
                />
                <NexusInput
                  label="Email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="john@acme.com"
                  data-testid="wizard-user-email"
                />
                <NexusInput
                  label="Password"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Minimum 6 characters"
                  data-testid="wizard-user-password"
                />
                <div className="flex justify-between pt-2">
                  <NexusButton variant="ghost" onClick={() => setStep(0)}>
                    <ChevronLeft size={16} /> Back
                  </NexusButton>
                  <NexusButton onClick={handleCreateUser} isLoading={isSubmitting} data-testid="wizard-create-user">
                    Create & Continue <ChevronRight size={16} />
                  </NexusButton>
                </div>
              </motion.div>
            )}

            {/* Step 3: Knowledge Base + Upload */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                {!createdKBId ? (
                  <>
                    <div className="p-3 rounded-lg bg-nexus-accent-violet/5 border border-nexus-accent-violet/20 text-sm text-nexus-text-secondary">
                      Create a foundation knowledge base and upload documents. The AI will parse, chunk, and embed them for retrieval-augmented generation.
                    </div>
                    <NexusInput
                      label="Knowledge Base Name"
                      value={kbForm.name}
                      onChange={(e) => setKbForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., EPC Best Practices"
                      data-testid="wizard-kb-name"
                    />
                    <NexusInput
                      label="Description (optional)"
                      value={kbForm.description}
                      onChange={(e) => setKbForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Describe the knowledge domain..."
                    />
                    <div className="flex justify-between pt-2">
                      <NexusButton variant="ghost" onClick={() => setStep(1)}>
                        <ChevronLeft size={16} /> Back
                      </NexusButton>
                      <div className="flex gap-2">
                        <NexusButton variant="ghost" onClick={() => setStep(3)} data-testid="wizard-skip-kb">
                          Skip for now
                        </NexusButton>
                        <NexusButton onClick={handleCreateKB} isLoading={isSubmitting} data-testid="wizard-create-kb">
                          Create KB <ChevronRight size={16} />
                        </NexusButton>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 text-sm text-nexus-text-secondary flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-green-400 flex-shrink-0" />
                      Knowledge base "{kbForm.name}" created. Upload documents below.
                    </div>

                    {/* Upload Zone */}
                    <div
                      onClick={!isUploading ? handleFileInput : undefined}
                      className={`border-2 border-dashed rounded-xl p-6 text-center transition-all
                        ${isUploading ? 'opacity-50 pointer-events-none border-nexus-border-subtle' : 'border-nexus-border-emphasized hover:border-nexus-accent-mint/50 cursor-pointer'}
                      `}
                      data-testid="wizard-upload-zone"
                    >
                      <Upload size={28} className="mx-auto mb-2 text-nexus-text-tertiary" />
                      <p className="text-sm text-nexus-text-secondary font-medium">Click to upload documents</p>
                      <p className="text-xs text-nexus-text-tertiary mt-1">PDF, DOCX, TXT — up to 50MB each</p>
                    </div>

                    {/* Upload Progress */}
                    {uploadQueue.length > 0 && (
                      <div className="space-y-2">
                        {uploadQueue.map((item, i) => {
                          const s = item.status;
                          const isComplete = s.stage === 'completed';
                          const isError = s.stage === 'error';
                          return (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-3 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle"
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <FileText size={16} className="text-nexus-accent-violet flex-shrink-0" />
                                <span className="text-sm text-nexus-text-primary truncate flex-1">{item.file.name}</span>
                                {isComplete && <CheckCircle2 size={16} className="text-green-400" />}
                                {isError && <XCircle size={16} className="text-red-400" />}
                                {!isComplete && !isError && <Loader2 size={16} className="animate-spin text-nexus-accent-mint" />}
                              </div>
                              <div className="w-full h-1.5 bg-nexus-bg-primary rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${s.progress || 0}%` }}
                                  className={`h-full rounded-full ${isError ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-nexus-accent-mint'}`}
                                />
                              </div>
                              <p className="text-xs text-nexus-text-tertiary mt-1">{s.detail || STAGE_LABELS[s.stage] || s.stage}</p>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex justify-between pt-2">
                      <NexusButton variant="ghost" onClick={() => { setCreatedKBId(null); setStep(1); }}>
                        <ChevronLeft size={16} /> Back
                      </NexusButton>
                      <NexusButton
                        onClick={() => setStep(3)}
                        disabled={isUploading}
                        data-testid="wizard-finish-kb"
                      >
                        {uploadQueue.length > 0 ? 'Continue' : 'Skip Uploads'} <ChevronRight size={16} />
                      </NexusButton>
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* Step 4: Done */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6 space-y-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                  className="w-20 h-20 rounded-full bg-green-500/10 border-2 border-green-500/30 flex items-center justify-center mx-auto"
                >
                  <CheckCircle2 size={40} className="text-green-400" />
                </motion.div>

                <div>
                  <h3 className="font-heading text-xl font-bold text-nexus-text-primary">Setup Complete!</h3>
                  <p className="text-sm text-nexus-text-secondary mt-2">
                    Your tenant <span className="font-medium text-nexus-accent-mint">{tenantForm.name}</span> is ready to use.
                  </p>
                </div>

                {/* Quick Links */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle cursor-pointer hover:border-nexus-accent-mint/30 transition-colors"
                    onClick={() => { handleFinish(); navigate('/portal/login'); }}
                    data-testid="wizard-goto-portal"
                  >
                    <User size={20} className="text-nexus-accent-mint mb-2" />
                    <p className="text-sm font-medium text-nexus-text-primary">Portal Login</p>
                    <p className="text-xs text-nexus-text-tertiary mt-0.5">{userForm.email}</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle cursor-pointer hover:border-nexus-accent-violet/30 transition-colors"
                    onClick={() => { handleFinish(); navigate(`/c/${createdShareCode || createdTenantSlug}`); }}
                    data-testid="wizard-goto-chat"
                  >
                    <Rocket size={20} className="text-nexus-accent-violet mb-2" />
                    <p className="text-sm font-medium text-nexus-text-primary">Consumer Chat</p>
                    <p className="text-xs text-nexus-text-tertiary mt-0.5">Public chat link</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                    className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle cursor-pointer hover:border-nexus-accent-amber/30 transition-colors"
                    onClick={() => { handleFinish(); navigate('/admin/knowledge'); }}
                    data-testid="wizard-goto-knowledge"
                  >
                    <Database size={20} className="text-nexus-accent-amber mb-2" />
                    <p className="text-sm font-medium text-nexus-text-primary">Knowledge Base</p>
                    <p className="text-xs text-nexus-text-tertiary mt-0.5">Manage foundation docs</p>
                  </motion.div>
                </div>

                <NexusButton onClick={handleFinish} className="w-full" data-testid="wizard-close">
                  Back to Dashboard
                </NexusButton>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
